import React from 'react';
import { PRIORITY_OPTIONS, STATUS_OPTIONS, CHANNEL_OPTIONS } from '../types';
import { useOrgTicketTypes, useOrgAllStages } from '../hooks/useOrgData';
import { ThemedSelect } from '@components/common/ThemedSelect';

const SELECT_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

interface Props {
  /** BRANCH_FIELD_OPTIONS valueType: ticketType | priority | status | channel | stage | text. */
  valueType: string;
  value: string;
  /** `label` is the human-readable name of the chosen value (e.g. a stage name). */
  onChange: (value: string, label: string) => void;
  ariaLabel?: string;
}

/**
 * The value picker for a condition field — a dropdown of real values (ticket
 * types, priorities, statuses, channels, pipeline stages) so nobody has to
 * type an internal id; free text for anything else. Shared by If / Branch and
 * Switch.
 */
export function FieldValueSelect({ valueType, value, onChange, ariaLabel }: Props) {
  const { ticketTypes, loading: typesLoading } = useOrgTicketTypes();
  const { stages, loading: stagesLoading } = useOrgAllStages();

  const select = (
    placeholder: string,
    options: Array<{ value: string; label: string }>,
    loading = false,
  ) => (
    <ThemedSelect
      aria-label={ariaLabel}
      className={SELECT_CLASS}
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v, options.find((o) => o.value === v)?.label ?? v);
      }}
    >
      <option value="">{placeholder}</option>
      {loading ? (
        <option disabled>Loading…</option>
      ) : (
        options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))
      )}
    </ThemedSelect>
  );

  switch (valueType) {
    case 'ticketType':
      return select('Select ticket type…', ticketTypes.map((t) => ({ value: t.id, label: t.name })), typesLoading);
    case 'stage':
      return select(
        'Select stage…',
        stages.map((s) => ({ value: s.id, label: `${s.pipelineName} / ${s.name}` })),
        stagesLoading,
      );
    case 'priority':
      return select('Select priority…', PRIORITY_OPTIONS.map((o) => ({ ...o })));
    case 'status':
      return select('Select status…', STATUS_OPTIONS.map((o) => ({ ...o })));
    case 'channel':
      return select('Select channel…', CHANNEL_OPTIONS.map((o) => ({ ...o })));
    default:
      return (
        <input
          type="text"
          aria-label={ariaLabel}
          className={SELECT_CLASS}
          value={value}
          onChange={(e) => onChange(e.target.value, e.target.value)}
          placeholder="Value…"
        />
      );
  }
}
